import React, { Component } from 'react';
import cookie from 'react-cookies';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
  loggedOut,
} from '../redux/actions';

import { loopbackGet } from './index';
import { propertiesToURIQuery } from '../utilities/objectHelper';

const noop = () => undefined;

function filterWithLimit(filter, limit, currentPage) {
  const filterExp = filter ? filter() : {};
  return limit ? { ...filterExp, skip: currentPage * limit, limit } : filterExp;
}

export default class LoopbackProvider extends Component {
  static loopbackHost;

  static loopbackToken;

  static store;

  static navigate;

  static currentLanguage = 'en';

  static getToken = () => cookie.load('token');

  static handleUnauthorization = () => {
    if (!this.store) {
      throw Error('store not configured');
    }

    this.store.dispatch(loggedOut());
    this.store.dispatch(this.navigate('/login?reason=unauthorize'));
  };

  constructor(props) {
    super(props);
    this.shouldRequestNumPages = true;
    this.numPages = -1;
    this.currentPage = 0;
  }

  componentDidMount() {
    const { target, model, targetName } = this.props;
    if (target) {
      target.setState({ [targetName || model]: { values: [], reload: noop, next: noop } });
    }
    this.fetchIfChanged(this.props);
  }

  componentDidUpdate() {
    this.fetchIfChanged(this.props);
  }

  fetchIfChanged({
    filter, model, callback, target, limit, path, targetName, locale, params,
  }) {
    const filterAndLimit = filterWithLimit(filter, limit, this.currentPage);
    const queryParams = { filter: filterAndLimit, locale, ...params };
    const queryParamsString = propertiesToURIQuery(queryParams);
    const url = `${model}/${path}?${queryParamsString}`;
    if (this.modelAndPath !== url) {
      this.modelAndPath = url;
      return this.fetchData(
        model,
        path,
        url,
        callback,
        target,
        targetName,
        queryParams,
      );
    }
    return Promise.resolve();
  }

  async fetchData(model, path, url, callback, target, targetName, queryParams) {
    const { limit } = queryParams.filter;
    if (limit !== undefined && this.shouldRequestNumPages) {
      try {
        const where = propertiesToURIQuery({
          ...queryParams,
          filter: _.omit(queryParams.filter, ['limit', 'skip']),
        });
        const { count } = await loopbackGet(`${model}/${path}/count?${where}`);
        if (count) {
          this.numPages = Math.ceil(count / limit);
        }
      } catch (error) {
        throw Error(`Error while retrieving the number of page items: ${error.stack}`);
      }
      this.shouldRequestNumPages = false;
    }

    try {
      const result = await loopbackGet(url);
      if (result.error) {
        throw Error(`Error wile fetching loopback data for ${url}: ${result.error}`);
      } else {
        const values = Array.isArray(result) ? result : [result];
        const data = {
          values,
          reload: () => this.fetchData(model, path, url, callback, target, targetName, queryParams),
          next: () => {
            if ((this.currentPage + 1) < this.numPages) {
              this.currentPage += 1;
              this.fetchIfChanged(this.props);
            }
          },
          previous: () => {
            if (this.currentPage > 0) {
              this.currentPage -= 1;
              this.fetchIfChanged(this.props);
            }
          },
          resetNumPages: () => {
            this.shouldRequestNumPages = true;
            this.currentPage = 0;
          },
          page: this.currentPage + 1,
          numPages: this.numPages,
        };

        if (callback) {
          callback(data);
        }

        if (target) {
          target.setState({ [targetName || model]: data });
        }
      }
    } catch (error) {
      throw Error(`Error while requesting the content of page ${error.stack}`);
    }
  }

  render() {
    return <div>{this.props.children}</div>;
  }
}

LoopbackProvider.propTypes = {
  model: PropTypes.string.isRequired,
  children: PropTypes.node,
  target: PropTypes.object,
  targetName: PropTypes.string,
};

LoopbackProvider.defaultProps = {
  children: <div />,
  target: undefined,
  targetName: undefined,
  // eslint-disable-next-line react/default-props-match-prop-types
  locale: LoopbackProvider.currentLanguage,
  // eslint-disable-next-line react/default-props-match-prop-types
  path: '',
  // eslint-disable-next-line react/default-props-match-prop-types
  params: {},
};
